/**
 * Shared ship/column loading (init + §10.2 loadColumns).
 *
 * Both the initial boot and the data-source switch load the ship list, and the
 * ship <select> drives column loading - so that logic lives here, not duplicated
 * in components. Everything writes to atoms; the columns/estimate/plot areas
 * (still ⬜) subscribe to those atoms when they land. Cross-area reactions go
 * through the seam stubs (`scheduleEstimate`, `syncToUrl`).
 */

import {
  getInfo,
  listShips,
  listShipNames,
  getColumns,
  getDateRange,
} from '../../api/endpoints.ts';
import type { Info } from '../../api/types.ts';
import {
  activeDataSource,
  ships,
  selectedShip,
  allColumns,
  selectedCols,
  shipDateRange,
  metadataUpdatedAt,
  fleetSummary,
  backendVersion,
  backendGitSha,
  shipsLoading,
  shipLoadError,
  shipMeta,
  catalogAvailable,
  filters,
  derivedSignals,
} from '../../state/store.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { showError } from '../../ui/error-box.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';
import { userMessage } from '../../api/errors.ts';

/**
 * Load the ship list for the active data source. EOS first polls `/info`
 * every 1.5s until the backend's metadata cache is ready (cold start),
 * captures `metadata_updated_at`, then fetches `/ships`. Another source is
 * asked for its own ships (`/ships?source=`). On failure the error is
 * surfaced and the list left empty.
 */
export async function loadShips(): Promise<void> {
  shipsLoading.set(true);
  shipLoadError.set(null);
  try {
    if (activeDataSource.value !== DEFAULT_SOURCE) {
      ships.set(await listShips(activeDataSource.value));
    } else {
      const fallback = (): Info => ({ ready: false, metadata_updated_at: null });
      let info = await getInfo().catch(fallback);
      // Read from the first answer, not after the wait below: whether this
      // deployment has a metadata database has nothing to do with whether the
      // parquet metadata cache has finished warming, and gating the catalog on
      // a cold start would report it unavailable while it is perfectly fine.
      catalogAvailable.set(info.features?.catalog === true);
      while (!info.ready) {
        await new Promise((r) => setTimeout(r, 1500));
        info = await getInfo().catch(fallback);
        catalogAvailable.set(info.features?.catalog === true);
      }
      metadataUpdatedAt.set(info.metadata_updated_at ?? null);
      fleetSummary.set(info.fleet ?? null);
      backendVersion.set(info.version ?? null);
      backendGitSha.set(info.git_sha ?? null);
      // Names are display-only, so a failure here must not stop the app -
      // `shipLabel` falls back to the code.
      const [list, names] = await Promise.all([
        listShips(activeDataSource.value),
        listShipNames().catch(() => ({})),
      ]);
      shipMeta.set(names);
      ships.set(list);
    }
  } catch (e) {
    const msg = userMessage(e);
    shipLoadError.set(msg);
    showError('Failed to load ships: ' + msg);
  } finally {
    shipsLoading.set(false);
  }
}

/**
 * Load a ship's columns and date range from the active source into atoms,
 * in parallel, then prune stale filters and schedule an estimate. No ship →
 * clear column/range state.
 */
export async function loadColumns(shipId: string | null): Promise<void> {
  selectedShip.set(shipId);

  if (!shipId) {
    allColumns.set([]);
    shipDateRange.set(null);
    selectedCols.set(new Set());
    return;
  }

  try {
    const [cols, dateRange] = await Promise.all([
      getColumns(shipId, activeDataSource.value),
      getDateRange(shipId, activeDataSource.value),
    ]);
    allColumns.set(cols);
    shipDateRange.set(dateRange);
    selectedCols.set(new Set());
    pruneFiltersForShip();
    scheduleEstimate();
  } catch (e) {
    const msg = userMessage(e);
    showError('Failed to load columns: ' + msg);
    allColumns.set([]);
  }
}

/**
 * Drop filter rows whose column is no longer available for the selected ship.
 * Derived-signal names (§10.4) stay valid filter targets, so they extend the
 * available set alongside the raw columns.
 */
export function pruneFiltersForShip(): void {
  const available = new Set(allColumns.value.map((c) => c.name));
  for (const d of derivedSignals.value) available.add(d.name);
  const kept = filters.value.filter((f) => available.has(f.column));
  if (kept.length !== filters.value.length) filters.set(kept);
}
